import { formatMoney, rewardToNext } from "@szerviz/core";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../context";
import type { AiCredits } from "../data";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";

/** "Még 3 szavazat vagy tipp, és 7 ingyen kreditet kap." (or that the month's maximum is reached). */
export function RewardProgress({ c }: { c: AiCredits }) {
  const { t } = useTranslation();
  const next = rewardToNext(c, c.rewardActions);
  return (
    <p className="reward-progress" role="status">
      <strong>{t("reward.title")}</strong>{" "}
      {next === null ? t("reward.max", { credits: c.rewardMonthlyMax }) : t("reward.progress", { count: next, credits: c.rewardCredits })}
      {c.reward > 0 && <> {t("reward.earned", { credits: c.reward })}</>}
    </p>
  );
}

/**
 * Okos lekérdezés credits of the workshop: what is left (whole credits, never forint), the purchased credits
 * with their expiry, the reward progress, and buying a credit pack (owner or manager; the price goes on the
 * monthly bill, confirmed once per purchase).
 */
export function AiCreditPanel({ canBuy }: { canBuy: boolean }) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const [c, setC] = useState<AiCredits | null>(null);
  const [pack, setPack] = useState<number | null>(null);
  const [bought, setBought] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const money = (huf: number) => formatMoney(huf, "HUF", i18n.language);
  const date = (iso: string) => new Date(iso).toLocaleDateString(i18n.language);

  const load = useCallback(async () => {
    try {
      setC(await store.aiCredits(workshop.id));
    } catch (err) {
      setError(err);
    }
  }, [store, workshop.id]);
  useEffect(() => void load(), [load]);
  if (!c) return <ErrorText error={error} />;

  const buy = async () => {
    if (pack === null) return;
    setError(null);
    try {
      await store.buyAiCredits(workshop.id, pack);
      setPack(null);
      setBought(true);
      await load();
    } catch (err) {
      setError(err);
    }
  };
  const low = c.available < c.costs.simple;
  const chosen = c.packs.find((p) => p.credits === pack) ?? null;
  const canStill = Math.max(c.purchaseLimit - c.boughtThisMonth, 0);

  return (
    <section className="ai-credit" aria-label={t("aiCredit.title")}>
      <h2>
        {t("aiCredit.title")} <Help id="aiCredit" />
      </h2>
      <p className={`credit-balance${low ? " warn-text" : ""}`}>
        {low ? t("aiCredit.empty") : t("aiCredit.available", { count: c.available })}
      </p>
      <p className="muted">
        {t(c.trial ? "aiCredit.detailsTrial" : "aiCredit.details", { allowance: c.allowance, left: c.allowanceLeft, used: c.usedThisMonth })}
      </p>
      {c.grants.length > 0 && (
        <ul className="credit-grants">
          {c.grants.map((g, i) => <li key={i}>{t(g.source === "gift" ? "aiCredit.grantGift" : "aiCredit.grant", { count: g.credits, date: date(g.grantedAt) })}</li>)}
        </ul>
      )}
      <p className="muted small">{t("aiCredit.costs", { simple: c.costs.simple, heavy: c.costs.heavy })}</p>
      <RewardProgress c={c} />
      <ErrorText error={error} />
      {bought && <p className="ok">{t("aiCredit.bought")}</p>}
      {canBuy && !c.trial && (
        <>
          <fieldset className="choice-cards">
            <legend>{t("aiCredit.buy")}</legend>
            {c.packs.map((p) => (
              <label key={p.credits} className={`choice-card${pack === p.credits ? " selected" : ""}`}>
                <input type="radio" name="aiPack" checked={pack === p.credits} onChange={() => { setPack(p.credits); setBought(false); }} />
                <span><strong>{t("aiCredit.pack", { count: p.credits })}</strong> <small>{t("aiCredit.packPrice", { price: money(p.netHuf) })}</small></span>
              </label>
            ))}
          </fieldset>
          <p className="muted small">{t("aiCredit.limitLeft", { count: canStill })}</p>
          {chosen && (
            <div className="paid-accept" role="group" aria-label={t("aiCredit.buy")}>
              <p>{t("aiCredit.confirm", { count: chosen.credits, price: money(chosen.netHuf) })}</p>
              <div className="actions">
                <button type="button" className="primary" onClick={() => void buy()}>{t("aiCredit.confirmButton", { count: chosen.credits })}</button>
                <button type="button" onClick={() => setPack(null)}>{t("common.cancel")}</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
